import Big from "big.js";
import { ValueByClaimType } from "../../shared/PortfolioTypes.ts";

/** What a case brings in a group of cases: on average, and at either end. */
export interface PerCase {
  average: number;
  lowest: number;
  highest: number;
  // the number of cases
  size: number;
}

/** The cases of a set of claim types, or all the cases. */
export type CaseGroup = { kind: "set"; claimTypes: string[] } | { kind: "all" };

/** What a case brings in a set of claim types, or over all the cases. */
export type PerCaseRow = PerCase & CaseGroup;

/** What a case of a set of claim types brings. */
const perCaseOf = ({
  value,
  size,
  lowest,
  highest,
}: ValueByClaimType): PerCase => ({
  average: new Big(value).div(size).toNumber(),
  lowest,
  highest,
  size,
});

/**
 * What a case brings over all the sets of claim types: all their values by
 * all their cases, between the lowest case of all and the highest. Without a
 * case there is nothing to tell.
 */
export function allCasesOf(
  sets: readonly ValueByClaimType[],
): PerCase | undefined {
  const size = sets.reduce((count, set) => count + set.size, 0);
  if (size === 0) return undefined;
  const value = sets.reduce((sum, set) => sum.add(set.value), new Big(0));
  return {
    average: value.div(size).toNumber(),
    lowest: Math.min(...sets.map(({ lowest }) => lowest)),
    highest: Math.max(...sets.map(({ highest }) => highest)),
    size,
  };
}

/**
 * What a case brings in each set of claim types and over all the cases, the
 * highest average first, as the totals of the sets are listed. A single set
 * holds all the cases, and is then the only row.
 */
export function perCaseRows(sets: readonly ValueByClaimType[]): PerCaseRow[] {
  const rows: PerCaseRow[] = sets.map((set) => ({
    kind: "set",
    claimTypes: set.claimTypes,
    ...perCaseOf(set),
  }));
  const all = allCasesOf(sets);
  if (all && sets.length > 1) rows.push({ kind: "all", ...all });
  return rows.sort((a, b) => b.average - a.average);
}
